import Table from '@/common/components/Table';
import TextField from '@/common/components/TextField';
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker';
import useDebounce from '@/common/hooks/useDebounce';
import { useFilterDate } from '@/common/hooks/useFilterDate';
import { useFilterParams } from '@/common/hooks/useFilterParams';
import { Mapper } from '@/common/services/mapper';
import { EquipmentData } from '@/features/equipments/types/equipments.type';
import { ArrowBigRightIcon, SearchIcon } from 'lucide-react';
import QueryString from 'qs';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { equipmentConsumptionHistoryColumns } from '../../../constants/equipmentConsumptionHistoryColumns.constant';
import { useEquipmentEvents } from '../../../hooks/useEquipmentEvents';
import { EquipmentEventsTransformer } from '../../../services/equipmentEventsTransformer';
import { UserEventData, UserEventTableItem } from '../../../types/userEvents.type';
import Button from '@/common/components/Button';
import useUserPermissions from '@/common/hooks/useUserPermissions';

interface ConsumptionHistoryProps {
  equipment: EquipmentData | undefined;
}

export const ConsumptionHistory = ({
  equipment,
}: ConsumptionHistoryProps) => {
  const { id: equipmentId } = useParams();

  const { hasUserPermissionWithProperty, isUserPermissionsPending } = useUserPermissions();

  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.consumptionHistorySearch || '');

  const { date, formattedDate } = useFilterDate(filterParams);

  const {
    data: equipmentEvents,
    meta,
    isFetching: equipmentEventsIsFetching,
  } = useEquipmentEvents({
    filters: {
      equipmentId,
      search,
      page: filterParams.page || 1,
      startDate: formattedDate.from,
      endDate: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer<UserEventData[], UserEventTableItem[]>({
      transformer: EquipmentEventsTransformer.tableTransformer(),
    }),
  });

  const categoryFieldForDisplayName = equipment?.model?.category?.field_for_display;

  const equipmentDisplayName = useMemo(() => {
    return (categoryFieldForDisplayName && equipment?.[categoryFieldForDisplayName as keyof typeof equipment] as string) ||
      equipment?.license_plate ||
      equipment?.official_name ||
      equipment?.serial_number ||
      'Não informado';
  }, [equipmentId, categoryFieldForDisplayName]);

  const reportUrl = QueryString.stringify({
    generator_klass: 'Reports::Client::EquipmentConsumptionsReport',
    q: {
      client_equipment_id_eq: {
        label: equipmentDisplayName,
        value: equipmentId,
      },
      created_at_gteq: date.from?.toISOString(),
      created_at_lteq: date.to?.toISOString(),
    },
  });

  const hasPermissionToCreateReport = useMemo(() => {
    return hasUserPermissionWithProperty({
      operationKlass: 'Client::Reports::Entries::Create',
      propertyKey: 'generator_klass',
      propertyValue: 'Reports::Client::EquipmentConsumptionsReport',
    });
  }, [isUserPermissionsPending]);

  return (
    <div className="flex flex-col gap-6">
      <TextField
        LeftIcon={<SearchIcon />}
        placeholder="Pesquisar"
        className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
        value={filterParams.consumptionHistorySearch || ''}
        onChange={(e: any) => {
          setSearch(e.target?.value);
          setFilterParams((prev) => ({
            ...prev,
            consumptionHistorySearch: e.target?.value,
          }));
        }}
      />

      <div className="flex items-center justify-between">
        <DatePickerWithRange
          date={date}
          setDate={(date) => setFilterParams((params) => ({ ...params, date }))}
        />

        <Link to={`/reports/create?${reportUrl}`}>
          <Button
            className="py-2 px-4"
            disabled={!hasPermissionToCreateReport}
            tooltipMessage={!hasPermissionToCreateReport ? 'Você não tem permissão' : undefined}
          >
            Exportar tabela

            <ArrowBigRightIcon size={18} />
          </Button>
        </Link>
      </div>

      <Table
        className="max-h-[calc(100vh-324px)]"
        columns={equipmentConsumptionHistoryColumns}
        isLoadingData={equipmentEventsIsFetching}
        rows={equipmentEvents}
        rowEmptyValue="--"
        page={filterParams.page || 1}
        setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        totalPages={meta.total_pages}
        totalCount={meta.total}
      />
    </div>
  );
};
